import type { RoundSpotlight, CashoutDonePayload, Obstacle } from '../../types/game.types';
import { BackgroundScroller } from './Background';
import { SkierEntity, SkierPlayerInfo, CHROMA_PALETTES, PresentationSlot, CrashPresentation } from './SkierEntity';
import { SeededLanePath, deriveDomainSeed } from './SeededPath';
import { getSlopeBounds } from './SlopeConfig';
import { maskPlayerName, shownRiderChroma } from '../../utils/displayName';
import { formatMoney } from '../../utils/money';

export interface LeaderboardEntry {
  playerId:          string;
  username:          string;
  chroma:            number;
  value:             number;
  status:            'racing' | 'cashed_out' | 'lost' | 'queued' | 'entering';
  cashoutMultiplier: number | null;
  betAmount:         number;
}

export interface WinnerInfo {
  playerId:          string;
  username:          string;
  chroma:            number;
  cashoutMultiplier: number;
  profit:            number;
  betAmount:         number;
}

export const MAX_VISIBLE_SLOTS = 5;

/**
 * Toggle this to false to restore the previous countdown feel where new skiers
 * enter from the top and flow into normal racing motion during STARTING.
 */
export const STARTING_LINEUP_ENABLED = true;

export class MultiSkierManager {
  private visible: SkierEntity[]     = [];
  private queue:   SkierPlayerInfo[] = [];
  private allInfo  = new Map<string, SkierPlayerInfo>();

  /** playerIds that have already cashed out — skiers for these are never created */
  private cashedOutIds = new Set<string>();
  private cashoutMultipliers = new Map<string, number>();

  private canvasWidth:  number;
  private canvasHeight: number;
  private startingLineupEnabled: boolean;

  private lanePaths:       SeededLanePath[] = [];
  private nextLaneIdx:     number           = 0;

  private crashSequenceStarted = false;
  private crashGeneration      = 0;
  private queueEntryPending    = false;
  private launchMode: 'live' | 'staged' | 'launching' = 'live';

  public winner:             WinnerInfo | null = null;
  private cashoutResults = new Map<string, CashoutDonePayload>();
  private authoritativeSpotlight: RoundSpotlight | null | undefined;

  private friendsMode = false;
  private friendsRotation = 0;
  setFriendsMode(enabled: boolean) { this.friendsMode = enabled; }

  private soloPlayerId: string | null = null;

  constructor(canvasWidth: number, canvasHeight: number, startingLineupEnabled = STARTING_LINEUP_ENABLED) {
    this.canvasWidth  = canvasWidth;
    this.canvasHeight = canvasHeight;
    this.startingLineupEnabled = startingLineupEnabled;
  }

  setSoloFilter(id: string | null) {
    this.soloPlayerId = id;
  }

  initRound(serverSeedHash: string, totalLanes: number = MAX_VISIBLE_SLOTS) {
    this.lanePaths   = [];
    this.nextLaneIdx = 0;
    for (let i = 0; i < totalLanes; i++) {
      const seed = deriveDomainSeed(serverSeedHash, `lane:${i}`);
      this.lanePaths.push(new SeededLanePath(seed, i, totalLanes));
    }
  }

  reset() {
    this.friendsRotation = 0;
    this.visible               = [];
    this.queue                 = [];
    this.allInfo.clear();
    this.cashedOutIds.clear();
    this.cashoutMultipliers.clear();
    this.winner                = null;
    this.cashoutResults.clear();
    this.authoritativeSpotlight = undefined;
    this.crashSequenceStarted  = false;
    this.crashGeneration++;
    this.queueEntryPending     = false;
    this.nextLaneIdx           = 0;
    this.launchMode            = 'live';
    // Note: soloPlayerId is intentionally NOT reset here — it's a persistent
    // user preference that survives round transitions.
  }

  resize(w: number, h: number) { this.canvasWidth = w; this.canvasHeight = h; }

  prepareForLaunchLineup() {
    if (!this.startingLineupEnabled) return;
    if (this.crashSequenceStarted) return;
    this.launchMode = 'staged';
    const active = this._activeInfos().slice(0, MAX_VISIBLE_SLOTS);
    for (const info of active) {
      const laneIdx = Math.max(0, active.findIndex(d => d.playerId === info.playerId));
      const slot = this._presentationSlot(laneIdx);
      const skier = this.visible.find(s => s.playerId === info.playerId && s.state !== 'EXITED');
      if (skier) {
        skier.setPresentationSlot(slot);
        skier.retargetStartLine(this._slotX(laneIdx));
      }
    }
  }

  beginLaunch() {
    this.launchMode = 'launching';
    this._reconcileRoster('top');
    for (const skier of this.visible) skier.beginLaunch();
  }

  addPlayer(info: SkierPlayerInfo) {
    if (this.allInfo.has(info.playerId)) {
      this.allInfo.set(info.playerId, { ...this.allInfo.get(info.playerId)!, ...info });
      this.refreshWinner();
      this._reconcileRoster(info.reconnect ? 'reconnect' : 'top');
      return;
    }
    this.allInfo.set(info.playerId, info);
    this.refreshWinner();

    // Don't create a sprite for players who already cashed out
    if (this.cashedOutIds.has(info.playerId)) return;

    this._reconcileRoster(info.reconnect ? 'reconnect' : 'top');
  }

  setSpotlight(result: RoundSpotlight | null) {
    this.authoritativeSpotlight = result;
    this.refreshWinner();
  }

  private refreshWinner() {
    if (this.authoritativeSpotlight !== undefined) {
      const result = this.authoritativeSpotlight;
      this.winner = result ? { ...result, chroma: result.chroma ?? this.allInfo.get(result.playerId)?.chroma ?? 0 } : null;
      return;
    }
    // Local simulations and live previews use the full roster too. Final live
    // results are replaced by the server's explicit spotlight (including null).
    let latest: CashoutDonePayload | undefined;
    for (const result of Array.from(this.cashoutResults.values())) {
      if (!this.allInfo.has(result.playerId) || result.playerId.startsWith('NPC_')) continue;
      const time = result.timestamp ?? result.multiplier;
      const priorTime = latest ? (latest.timestamp ?? latest.multiplier) : -Infinity;
      if (!latest || time > priorTime || (time === priorTime && (result.betId ?? result.playerId) > (latest.betId ?? latest.playerId))) latest = result;
    }
    const info = latest && this.allInfo.get(latest.playerId);
    this.winner = latest && info ? {
      playerId: info.playerId, username: info.username, chroma: info.chroma,
      cashoutMultiplier: latest.multiplier, betAmount: info.betAmount,
      profit: Number.isFinite(latest.profit) ? latest.profit : (latest.multiplier - 1) * info.betAmount,
    } : null;
  }

  onCashout(playerId: string, multiplier: number, result?: CashoutDonePayload) {
    const replay = this.cashedOutIds.has(playerId);
    this.cashedOutIds.add(playerId);
    this.cashoutMultipliers.set(playerId, multiplier);
    if (!replay) this.cashoutResults.set(playerId, result ?? { playerId, multiplier, profit: NaN });
    // Legacy simulation calls have no server profit; derive it when the roster arrives.
    const stored = this.cashoutResults.get(playerId);
    const info = this.allInfo.get(playerId);
    if (stored && !Number.isFinite(stored.profit) && info) stored.profit = (multiplier - 1) * info.betAmount;
    this.refreshWinner();
    this.queue = this.queue.filter(q => q.playerId !== playerId);
    if (replay) return;
    const skier = this.visible.find(s => s.playerId === playerId);
    if (!skier || (skier.state !== 'RACING' && skier.state !== 'ENTERING' && skier.state !== 'STAGED')) {
      this._reconcileRoster('side');
      return;
    }
    skier.cashout(multiplier);
    this.queueEntryPending = this.queue.length > 0;
  }

  beginCrashSequence(presentation: CrashPresentation = 'explosion') {
    if (this.crashSequenceStarted) return;
    this.crashSequenceStarted = true;
    const generation = ++this.crashGeneration;
    const racers = this.visible.filter(s => s.state === 'RACING' || s.state === 'ENTERING');
    const ordered = presentation === 'avalanche' || presentation === 'snowball'
      ? [...racers].sort((a, b) => a.getY() - b.getY())
      : presentation === 'cliff'
      ? [...racers].sort((a, b) => b.getY() - a.getY())
      : presentation === 'snow_slab'
      ? [...racers].sort((a, b) => a.getY() - b.getY())
      : racers;
    ordered.forEach((skier, i) => setTimeout(() => {
      if (!this.crashSequenceStarted || generation !== this.crashGeneration) return;
      skier.beginCrash(presentation);
    }, presentation === 'avalanche' ? 180 + i * 55
      : presentation === 'snowball' ? 420 + i * 40
      : presentation === 'snow_slab' ? 330 + i * 90
      : presentation === 'cliff' ? 300 + i * 44
      : i * 95));
  }

  update(obstacles: Obstacle[], background: BackgroundScroller, elapsedSeconds = 0) {
    const rotation = Math.floor(Math.max(0, elapsedSeconds) / 8);
    if (this.friendsMode && rotation !== this.friendsRotation) {
      this.friendsRotation = rotation;
      this._reconcileRoster('side');
    }
    for (const skier of this.visible) skier.update(obstacles, background, elapsedSeconds);


    const racers = this.visible.filter(s => s.state === 'RACING' || s.state === 'ENTERING');
    for (const racer of racers) racer.blendTowardSlotAnchor();

    for (let i = 0; i < racers.length; i++) {
      for (let j = i + 1; j < racers.length; j++) {
        const a = racers[i], b = racers[j];
        const dx = b.getCenterX() - a.getCenterX();
        const dy = b.getCenterY() - a.getCenterY();
        const dist = Math.max(1, Math.hypot(dx, dy));
        const minDist = Math.max(a.getSpacingRadius(), b.getSpacingRadius());
        if (dist < minDist) {
          const pressure = (minDist - dist) / minDist;
          const nx = dx / dist;
          const ny = dy / dist;
          const pushX = Math.min(1.45, pressure * 2.2);
          const pushY = Math.min(0.80, pressure * 1.2);
          a.applyFormationForce(-nx * pushX, -ny * pushY);
          b.applyFormationForce( nx * pushX,  ny * pushY);
        }
      }
    }

    const beforeVisible = this.visible.length;
    this.visible = this.visible.filter(s => s.state !== 'EXITED' && !s.isExplosionDone());
    if (this.visible.length !== beforeVisible && this.queueEntryPending) {
      this.queueEntryPending = false;
      this._reconcileRoster('side');
    }
  }

  drawTracks(ctx: CanvasRenderingContext2D, currentMultiplier = 1) {
    const skiers = this.soloPlayerId
      ? this.visible.filter(s => s.playerId === this.soloPlayerId)
      : this.visible;
    for (const s of skiers) s.drawTracks(ctx, currentMultiplier);
  }

  drawBodies(ctx: CanvasRenderingContext2D, mult: number) {
    const skiers = this.soloPlayerId
      ? this.visible.filter(s => s.playerId === this.soloPlayerId)
      : this.visible;
    [...skiers].sort((a,b)=>a.getY()-b.getY()).forEach(s=>s.drawBody(ctx,mult));
  }

  drawWinnerBanner(ctx: CanvasRenderingContext2D, canvasW: number, alpha: number) {
    if (!this.winner||alpha<=0) return;
    const w=this.winner, pal=CHROMA_PALETTES[shownRiderChroma(w.chroma, w.playerId) ?? w.chroma] ?? CHROMA_PALETTES[0], BH=76, BY=10;
    ctx.save(); ctx.globalAlpha=alpha;
    ctx.fillStyle='rgba(4,8,14,.94)'; ctx.fillRect(10,BY,canvasW-20,BH);
    ctx.strokeStyle=pal.label+'55'; ctx.lineWidth=1;
    ctx.beginPath(); ctx.moveTo(0,BY); ctx.lineTo(canvasW,BY); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(0,BY+BH); ctx.lineTo(canvasW,BY+BH); ctx.stroke();
    ctx.textAlign='center'; ctx.textBaseline='middle'; const midY=BY+BH/2;
    ctx.font='600 12px JetBrains Mono,monospace'; ctx.fillStyle='#f4fbff';
    ctx.fillText('LAST OUT',canvasW/2,midY-23);
    ctx.font='bold 22px Barlow Condensed,Arial'; ctx.fillStyle=pal.label;
    ctx.shadowBlur=0; ctx.fillText(maskPlayerName(w.username, w.playerId),canvasW/2,midY,canvasW-40);
    ctx.shadowBlur=0; ctx.font='600 12px JetBrains Mono,monospace'; ctx.fillStyle='#dceaf2';
    ctx.fillText(`${w.cashoutMultiplier.toFixed(2)}x  ·  +${formatMoney(w.profit)} profit`,canvasW/2,midY+24,canvasW-40);
    ctx.restore();
  }

  getLeaderboard(currentMultiplier: number): LeaderboardEntry[] {
    const entries: LeaderboardEntry[] = [];
    this.allInfo.forEach((info) => {
      const skier  = this.visible.find(s => s.playerId === info.playerId);
      const queued = this.queue.some(q => q.playerId === info.playerId);
      let status: LeaderboardEntry['status'], value=0, cashoutMultiplier: number|null=null;

      if (queued)                                    { status='queued';     value=info.betAmount; }
      else if (this.cashedOutIds.has(info.playerId)) { status='cashed_out'; cashoutMultiplier=skier?.cashoutMultiplier??this.cashoutMultipliers.get(info.playerId)??null; value=info.betAmount*(cashoutMultiplier??1); }
      else if (!skier)                               { status=this.crashSequenceStarted?'lost':'queued'; value=info.betAmount; }
      else if (skier.cashoutMultiplier!==null)       { status='cashed_out'; cashoutMultiplier=skier.cashoutMultiplier; value=info.betAmount*skier.cashoutMultiplier; }
      else if (skier.state==='CRASHED')              { status='lost';       value=0; }
      else if (skier.state==='ENTERING')             { status='entering';   value=info.betAmount; }
      else                                           { status='racing';     value=info.betAmount*currentMultiplier; }

      entries.push({ playerId:info.playerId, username:info.username, chroma:info.chroma, value, status, cashoutMultiplier, betAmount:info.betAmount });
    });

    const order: Record<LeaderboardEntry['status'],number> = { cashed_out:0,racing:1,entering:1,queued:2,lost:3 };
    return entries.sort((a,b)=>{const od=order[a.status]-order[b.status]; return od!==0?od:b.value-a.value;});
  }

  getVisibleCount()  { return this.visible.length; }
  getQueueCount()    { return this.queue.length; }
  getTotalCount()    { return this.allInfo.size; }
  matchesRoster(playerIds: string[]) {
    const expected = new Set(playerIds.filter(Boolean));
    if (expected.size !== this.allInfo.size) return false;
    for (const playerId of Array.from(expected)) {
      if (!this.allInfo.has(playerId)) return false;
    }
    return true;
  }
  getAllPlayerIds() { return Array.from(this.allInfo.keys()); }
  getVisiblePlayerIds() {
    const visibleIds = new Set(
      this.visible
        .filter(s => s.state !== 'CASHING_OUT' && s.state !== 'EXITED')
        .map(s => s.playerId)
    );
    return this._activeInfos().filter(info => visibleIds.has(info.playerId)).map(info => info.playerId);
  }
  getQueuedPlayerIds()  { return this.queue.map(q => q.playerId); }
  hasAnyBettors()    { return this.allInfo.size > 0; }
  hasAnyRacers()     { return this.visible.some(s => s.state==='RACING'||s.state==='ENTERING'); }

  private _activeInfos(): SkierPlayerInfo[] {
    return Array.from(this.allInfo.values())
      .filter(info => !this.cashedOutIds.has(info.playerId))
      .sort((a, b) => {
        if (this.friendsMode) {
          if (!!a.isSelf !== !!b.isSelf) return a.isSelf ? -1 : 1;
          return a.playerId.localeCompare(b.playerId);
        }
        const betDiff = b.betAmount - a.betAmount;
        if (betDiff !== 0) return betDiff;
        return a.playerId.localeCompare(b.playerId);
      });
  }

  private _reconcileRoster(entry: 'top'|'side'|'reconnect' = 'top') {
    if (this.crashSequenceStarted) return;

    const active = this._activeInfos();
    let desired = active.slice(0, MAX_VISIBLE_SLOTS);
    if (this.friendsMode && active.length > MAX_VISIBLE_SLOTS) {
      const self = active.find(info => info.isSelf);
      const others = active.filter(info => !info.isSelf);
      const slots = MAX_VISIBLE_SLOTS - (self ? 1 : 0);
      const offset = (this.friendsRotation * slots) % others.length;
      const rotated = [...others.slice(offset), ...others.slice(0, offset)];
      desired = [...(self ? [self] : []), ...rotated.slice(0, slots)];
    }
    const desiredIds = new Set(desired.map(info => info.playerId));

    this.visible = this.visible.filter(skier => {
      if (skier.state === 'CASHING_OUT' || skier.state === 'CRASHED') return true;
      return desiredIds.has(skier.playerId);
    });

    for (const info of desired) {
      const laneIdx = Math.max(0, desired.findIndex(d => d.playerId === info.playerId));
      const slot = this._presentationSlot(laneIdx);
      const alreadyVisible = this.visible.find(s => s.playerId === info.playerId && s.state !== 'EXITED');
      if (alreadyVisible) {
        alreadyVisible.setPresentationSlot(slot);
        if (this.launchMode === 'staged' && this.startingLineupEnabled) {
          alreadyVisible.retargetStartLine(this._slotX(laneIdx));
        }
      } else {
        this._spawnSkier(info, laneIdx, entry, slot);
      }
    }

    const visibleActiveIds = new Set(
      this.visible
        .filter(s => s.state !== 'CASHING_OUT' && s.state !== 'CRASHED' && s.state !== 'EXITED')
        .map(s => s.playerId)
    );
    this.queue = active.filter(info => !visibleActiveIds.has(info.playerId));
  }

  private _slotX(index: number): number {
    const { left, right } = getSlopeBounds(this.canvasWidth);
    const slotW = (right - left) / MAX_VISIBLE_SLOTS;
    return left + slotW * index + slotW / 2;
  }

  private _presentationSlot(index: number): PresentationSlot {
    const total = MAX_VISIBLE_SLOTS;
    const xOffsets = [0.12, -0.10, 0.04, -0.18, 0.18];
    const yOffsets = [-20, 8, -4, 24, -32];
    const anchorNormX = Math.max(0.08, Math.min(0.92, (index + 0.5) / total + xOffsets[index % xOffsets.length]));
    const seed = (index + 1) * 1.917;
    return {
      index,
      total,
      anchorNormX,
      targetY: 96 + yOffsets[index % yOffsets.length],
      swayAmp: 16 + (index % 3) * 5,
      swayFreq: 0.10 + (index % 4) * 0.025,
      swayPhase: seed,
      spacingRadius: 66 + (index % 2) * 8,
    };
  }

  private _spawnSkier(info: SkierPlayerInfo, laneIdx: number, entry: 'top'|'side'|'reconnect', slot = this._presentationSlot(laneIdx)) {
    const skier = new SkierEntity(info, this.canvasWidth, this.canvasHeight);
    if (this.lanePaths.length > 0) skier.setPath(this.lanePaths[laneIdx % this.lanePaths.length]);
    skier.setPresentationSlot(slot);

    if (entry === 'reconnect' && this.launchMode === 'staged' && this.startingLineupEnabled) {
      skier.stageAtStartLine(this._slotX(laneIdx));
    } else if (entry === 'reconnect') {
      skier.placeForReconnect(this._slotX(laneIdx));
    } else if (entry === 'top' && this.launchMode === 'staged' && this.startingLineupEnabled) {
      skier.rollToStartLine(this._slotX(laneIdx));
    } else if (entry === 'top') {
      skier.enterFromTop(this._slotX(laneIdx));
    } else {
      skier.enterFromPromotedTop(this._slotX(laneIdx));
    }
    this.visible.push(skier);
  }

  private _promoteFromQueue() {
    if (this.crashSequenceStarted) return;
    this._reconcileRoster('side');
  }
}
